import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SalesSummaryItemResponse, SalesSummaryResponse } from '../../api/types';
import { Columns, type Column } from '../../components/charts/Columns';
import { RankedBars, type RankedBar } from '../../components/charts/RankedBars';
import { bucketValues, daysBetween, granularityFor } from '../../components/charts/buckets';
import { bucketLabel, bucketTitle } from '../../components/charts/dates';
import { compact, count, pounds } from '../../i18n/format';
import { useLocalisedName } from '../../i18n/localisedName';
import { magnitude } from '../catalog/money';

/** Past this many, "what sold" shows the rest behind "Show all". */
const FIRST_ITEMS = 8;

/**
 * The Sales screen's charts (CR-34), from the money summary over the chosen days.
 *
 * The strip answers "how much": sold, taken, still owed, refunded, and what discounts cost. The
 * columns answer "when", day by day (weeks or months over a long range). The bars answer "how" and
 * "what": how patients paid, and which services and packages brought the money in, or which kinds
 * of service. Over every branch, a branch chart compares them.
 *
 * Every figure is the day summary's own (see the endpoint), so one day here is the number the cash
 * summary below shows for that day. Amounts include VAT, as the receipt does; the strip says so.
 */
export function SalesOverview({
  summary,
  from,
  to,
  branchName,
  showBranches,
}: {
  readonly summary: SalesSummaryResponse;
  readonly from: string;
  readonly to: string;
  readonly branchName: (id: string | undefined) => string;
  /** Over every branch, so the branch chart means something. */
  readonly showBranches: boolean;
}) {
  const { t, i18n } = useTranslation();
  const localised = useLocalisedName();
  const [itemView, setItemView] = useState<'item' | 'type'>('item');

  const sold = magnitude(summary.sold);
  const discount = magnitude(summary.discount);
  const outstanding = magnitude(summary.outstanding);
  const saleCount = summary.saleCount ?? 0;

  const granularity = granularityFor(daysBetween(from, to));
  const columns: Column[] = bucketValues(
    (summary.byDay ?? []).map((day) => ({
      date: day.day ?? '',
      values: [magnitude(day.sold), magnitude(day.taken), magnitude(day.refunded), day.saleCount ?? 0],
    })),
    granularity,
  ).map((bucket) => {
    const [daySold = 0, dayTaken = 0, dayRefunded = 0, daySales = 0] = bucket.values;
    return {
      key: bucket.start,
      label: bucketLabel(bucket, granularity, i18n.language),
      description: t('overview.sales.columnDescription', {
        when: bucketTitle(bucket, granularity, i18n.language, (first, last) =>
          t('overview.weekOf', { from: first, to: last }),
        ),
        sold: pounds(daySold),
        count: daySales,
        taken: pounds(dayTaken),
        refunded: pounds(dayRefunded),
      }),
      segments: [{ value: daySold, colour: 'var(--chart-1)' }],
    };
  });

  const methods: RankedBar[] = (summary.byMethod ?? [])
    .filter((row) => magnitude(row.total) > 0)
    .map((row) => ({
      key: row.method ?? '',
      label: t(`checkout.methods.${row.method ?? 'CASH'}`),
      value: magnitude(row.total),
    }));
  const items: RankedBar[] = (summary.byItem ?? []).map((item, index) => ({
    key: `${item.kind ?? ''}:${item.id ?? item.nameEn ?? index}`,
    label:
      item.kind === 'PACKAGE'
        ? t('overview.sales.packageItem', { name: localised.name({ nameEn: item.nameEn, nameAr: item.nameAr }) })
        : localised.name({ nameEn: item.nameEn, nameAr: item.nameAr }),
    value: magnitude(item.gross),
  }));
  const types = byType(summary.byItem ?? [], (item) => {
    if (item.kind === 'PACKAGE') {
      return t('overview.sales.packages');
    }
    if (item.kind === 'OTHER') {
      return t('overview.sales.otherItems');
    }
    return item.category ? t(`catalog.categories.${item.category}`) : t('overview.noType');
  });
  const branches: RankedBar[] = (summary.byBranch ?? []).map((row) => ({
    key: row.branchId ?? '',
    label: branchName(row.branchId),
    value: magnitude(row.sold),
  }));

  return (
    <div className="overview">
      <section className="measures" aria-label={t('overview.sales.strip')}>
        <div className="measure measure--lead">
          <span className="measure__label">{t('overview.sales.sold', { currency: 'EGP' })}</span>
          <span className="measure__value">{count(Math.round(sold))}</span>
          <span className="measure__note">{t('overview.sales.saleCount', { count: saleCount })}</span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.sales.taken', { currency: 'EGP' })}</span>
          <span className="measure__value">{count(Math.round(magnitude(summary.taken)))}</span>
          <span className="measure__note">{t('overview.sales.takenNote')}</span>
        </div>
        <div className={outstanding > 0 ? 'measure measure--alert' : 'measure'}>
          <span className="measure__label">{t('overview.sales.owed', { currency: 'EGP' })}</span>
          <span className="measure__value">{count(Math.round(outstanding))}</span>
          <span className="measure__note">{t('overview.sales.owedNote')}</span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.sales.refunded', { currency: 'EGP' })}</span>
          <span className="measure__value">{count(Math.round(magnitude(summary.refunded)))}</span>
          <span className="measure__note">
            {t('overview.sales.voided', { count: summary.voidedSales ?? 0 })}
          </span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.sales.discounts', { currency: 'EGP' })}</span>
          <span className="measure__value">{count(Math.round(discount))}</span>
          <span className="measure__note">
            {t('overview.sales.discountedSales', { count: summary.discountedSales ?? 0, total: saleCount })}
          </span>
        </div>
      </section>

      <section className="panel">
        <div className="panel__heading">
          <h2>{t('overview.sales.perDay')}</h2>
          <p className="muted">
            {t(`overview.per.${granularity}`)} · {t('overview.sales.includesVat')}
          </p>
        </div>
        <Columns columns={columns} label={t('overview.sales.perDay')} formatTick={compact} />
      </section>

      <div className="overview-grid">
        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.sales.methods')}</h2>
            <p className="muted">{t('overview.sales.methodsNote')}</p>
          </div>
          {methods.length > 0 ? (
            <RankedBars rows={methods} format={pounds} />
          ) : (
            <p className="muted">{t('overview.sales.nothingTaken')}</p>
          )}
        </section>

        <section className="panel">
          <div className="panel__header">
            <div className="panel__heading">
              <h2>{t('overview.sales.whatSold')}</h2>
              <p className="muted">{t('overview.sales.whatSoldNote')}</p>
            </div>
            <div className="tabs" role="tablist" aria-label={t('overview.sales.whatSold')}>
              {(['item', 'type'] as const).map((view) => (
                <button
                  key={view}
                  type="button"
                  role="tab"
                  aria-selected={itemView === view}
                  className={itemView === view ? 'tab tab--active' : 'tab'}
                  onClick={() => setItemView(view)}
                >
                  {t(`overview.sales.view.${view}`)}
                </button>
              ))}
            </div>
          </div>
          {items.length === 0 ? (
            <p className="muted">{t('overview.sales.nothingSold')}</p>
          ) : (
            <RankedBars
              key={itemView}
              rows={itemView === 'item' ? items : types}
              format={pounds}
              firstRows={FIRST_ITEMS}
            />
          )}
        </section>

        {showBranches && branches.length > 1 ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.sales.byBranch')}</h2>
              <p className="muted">{t('overview.sales.byBranchNote')}</p>
            </div>
            <RankedBars rows={branches} format={pounds} />
          </section>
        ) : null}
      </div>
    </div>
  );
}

/** What sold folded into kinds: each service category, packages, and anything else. */
function byType(items: readonly SalesSummaryItemResponse[], label: (item: SalesSummaryItemResponse) => string) {
  const folded = new Map<string, RankedBar>();
  for (const item of items) {
    const name = label(item);
    const running = folded.get(name);
    folded.set(name, { key: name, label: name, value: (running?.value ?? 0) + magnitude(item.gross) });
  }
  return [...folded.values()].sort((a, b) => b.value - a.value);
}
